// Messages between a page and its preview frame (/preview/frame.html). The frame is a sandboxed iframe with an opaque
// origin (sandbox="allow-scripts", no allow-same-origin) where asset code runs; it never gets a session token and can't
// read the site's storage or DOM. Both sides accept only these shapes, from the expected window, and drop the rest.
//
// page → frame: init (once), params (asset) or avatar / focus / play / portrait (avatar creator); a zone's scene takes init only
// frame → page: ready, note, stats, loaded (a scene: after its first frame), portrait (the avatar creator's picture)

import type { Manifest, Stats, Value } from "../store.ts";

export const FRAME_PATH = "/preview/frame.html";

/** The only places the frame may load package files from (also its CSP's connect-src and script-src). */
export const FILE_PREFIXES = ["/api/store-files/", "/api/store-preview/", "/store/packages/", "/api/store/v1/dl/"] as const;
/** Dev-only fixtures (serve.ts mounts them only with SITE_TEST_FIXTURES=1). */
export const FIXTURE_PREFIX = "/test-fixtures/";

/** Zone hero scenes the frame knows (src/frame/scenes/): first-party code that loads only baked GLBs. */
export const SCENES = ["open-haunt"] as const;
export type SceneId = (typeof SCENES)[number];

/** Backdrops the asset view knows. */
export const ASSET_STAGES = ["dusk", "workshop"] as const;
export type AssetStage = (typeof ASSET_STAGES)[number];

/** What the avatar creator's preview plays: idle, walking on the spot, or a one-shot (sdk/avatar's procedural clips). */
export const AVATAR_CLIPS = ["idle", "walk", "wave", "jump", "cheer", "clap", "dance"] as const;
export type AvatarClip = (typeof AVATAR_CLIPS)[number];
/** A portrait comes back as a JPEG data URL, at most this long (accounts takes ≤ 256 KB of bytes). */
export const MAX_PORTRAIT_URL = 340_000;
const PORTRAIT_PREFIX = "data:image/jpeg;base64,";

export type AvatarSpecMsg = { body: { id: string; params?: Record<string, Value> }; outfit: { id: string; params?: Record<string, Value> }[] };

export type ToFrame =
  // live: run the asset's code with params; baked: its default bake only; preview: the store's public low-detail model
  // (base is /api/store-preview/<id>/<v>/: preview.glb plus a code-free asset.json). baked and preview never run asset code.
  // stage: the look of the backdrop ("dusk" when absent; "workshop" is the Assets zone's lamplit bench, always turning)
  | { t: "init"; kind: "asset"; mode: "live" | "baked" | "preview"; base: string; manifest: Manifest; bakedUrl?: string; bodyBase: string; stage?: AssetStage; loop?: number }
  | { t: "init"; kind: "avatar"; packages: string }
  | { t: "init"; kind: "scene"; scene: SceneId; packages: string; quality: "high" | "low" }
  | { t: "params"; params: Record<string, Value> }
  | { t: "avatar"; spec: AvatarSpecMsg }
  | { t: "focus"; view: "full" | "face" }
  | { t: "play"; clip: AvatarClip }
  | { t: "portrait" };

export type FromFrame = { t: "ready" } | { t: "note"; text: string } | { t: "stats"; stats: Stats } | { t: "loaded"; headY?: number } | { t: "portrait"; jpeg: string | null };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const isValue = (v: unknown): v is Value => typeof v === "string" || typeof v === "boolean" || (typeof v === "number" && Number.isFinite(v));

/** A same-site path under one of the package file routes, with no way out of it. */
export function allowedFilePath(p: unknown, fixtures = false): p is string {
  if (typeof p !== "string" || p.length > 2048 || !p.startsWith("/") || p.startsWith("//")) return false;
  if (/[\\\0]|\/\.\.?(\/|$)|%2e%2e|%2f/i.test(p)) return false;
  return FILE_PREFIXES.some((x) => p.startsWith(x)) || (fixtures && p.startsWith(FIXTURE_PREFIX));
}

function params(v: unknown): Record<string, Value> | null {
  if (!isObj(v)) return null;
  const out: Record<string, Value> = {};
  const entries = Object.entries(v);
  if (entries.length > 64) return null;
  for (const [k, x] of entries) {
    if (!/^[A-Za-z][A-Za-z0-9_]{0,40}$/.test(k) || !isValue(x) || (typeof x === "string" && x.length > 200)) return null;
    out[k] = x;
  }
  return out;
}

const PKG_ID = /^[a-z0-9][a-z0-9-]{0,63}\/[a-z0-9][a-z0-9-]{0,63}$/;
function spec(v: unknown): AvatarSpecMsg | null {
  if (!isObj(v) || !isObj(v.body) || typeof v.body.id !== "string" || !PKG_ID.test(v.body.id) || !Array.isArray(v.outfit) || v.outfit.length > 16) return null;
  const bodyParams = v.body.params === undefined ? {} : params(v.body.params);
  if (!bodyParams) return null;
  const outfit: AvatarSpecMsg["outfit"] = [];
  for (const o of v.outfit) {
    if (!isObj(o) || typeof o.id !== "string" || !PKG_ID.test(o.id)) return null;
    const p = o.params === undefined ? {} : params(o.params);
    if (!p) return null;
    outfit.push({ id: o.id, params: p });
  }
  return { body: { id: v.body.id, params: bodyParams }, outfit };
}

/** What the frame accepts from its parent page. */
export function parseToFrame(d: unknown, fixtures = false): ToFrame | null {
  if (!isObj(d)) return null;
  if (d.t === "init" && d.kind === "asset") {
    if ((d.mode !== "live" && d.mode !== "baked" && d.mode !== "preview") || !allowedFilePath(d.base, fixtures) || !String(d.base).endsWith("/") || !allowedFilePath(d.bodyBase, fixtures)) return null;
    if (d.bakedUrl !== undefined && !allowedFilePath(d.bakedUrl, fixtures)) return null;
    const m = d.manifest;
    if (!isObj(m) || typeof m.id !== "string" || !PKG_ID.test(m.id) || typeof m.version !== "string") return null;
    if (d.mode === "preview" && !String(d.base).startsWith("/api/store-preview/")) return null;
    if (d.stage !== undefined && !ASSET_STAGES.includes(d.stage as AssetStage)) return null;
    const out: ToFrame = { t: "init", kind: "asset", mode: d.mode, base: d.base as string, manifest: m as unknown as Manifest, bakedUrl: d.bakedUrl as string | undefined, bodyBase: d.bodyBase as string };
    if (d.stage !== undefined) out.stage = d.stage as AssetStage;
    // capture only (the launcher's loop): a whole number of seconds' cycle on the workshop stage
    if (d.loop !== undefined) {
      if (typeof d.loop !== "number" || !(d.loop >= 2 && d.loop <= 60) || d.stage !== "workshop" || d.mode !== "live") return null;
      out.loop = d.loop;
    }
    return out;
  }
  if (d.t === "init" && d.kind === "scene") {
    if (!SCENES.includes(d.scene as SceneId) || !allowedFilePath(d.packages, fixtures) || (d.quality !== "high" && d.quality !== "low")) return null;
    return { t: "init", kind: "scene", scene: d.scene as SceneId, packages: d.packages as string, quality: d.quality };
  }
  if (d.t === "init" && d.kind === "avatar") return allowedFilePath(d.packages, fixtures) ? { t: "init", kind: "avatar", packages: d.packages as string } : null;
  if (d.t === "params") {
    const p = params(d.params);
    return p ? { t: "params", params: p } : null;
  }
  if (d.t === "avatar") {
    const s = spec(d.spec);
    return s ? { t: "avatar", spec: s } : null;
  }
  if (d.t === "focus" && (d.view === "full" || d.view === "face")) return { t: "focus", view: d.view };
  if (d.t === "play" && AVATAR_CLIPS.includes(d.clip as AvatarClip)) return { t: "play", clip: d.clip as AvatarClip };
  if (d.t === "portrait" && Object.keys(d).length === 1) return { t: "portrait" };
  return null;
}

/** What the page accepts from its frame. Text is shown as text, numbers are checked. */
export function parseFromFrame(d: unknown): FromFrame | null {
  if (!isObj(d)) return null;
  if (d.t === "ready" && Object.keys(d).length === 1) return { t: "ready" };
  if (d.t === "note" && typeof d.text === "string") return { t: "note", text: d.text.slice(0, 300) };
  if (d.t === "stats" && isObj(d.stats)) {
    const s: Stats = {};
    const n = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v < 1e9;
    if (n(d.stats.triangles)) s.triangles = Math.floor(d.stats.triangles as number);
    if (n(d.stats.drawCalls)) s.drawCalls = Math.floor(d.stats.drawCalls as number);
    return { t: "stats", stats: s };
  }
  if (d.t === "portrait") {
    if (d.jpeg === null) return { t: "portrait", jpeg: null };
    const ok = typeof d.jpeg === "string" && d.jpeg.length <= MAX_PORTRAIT_URL && d.jpeg.startsWith(PORTRAIT_PREFIX) && /^[A-Za-z0-9+/]+={0,2}$/.test(d.jpeg.slice(PORTRAIT_PREFIX.length));
    return ok ? { t: "portrait", jpeg: d.jpeg as string } : null;
  }
  if (d.t === "loaded") return { t: "loaded", headY: typeof d.headY === "number" && Number.isFinite(d.headY) ? d.headY : undefined };
  return null;
}
